import { AxiosResponse } from 'axios';

import { IAddress } from 'models/address';
import { ExportRequestArgs, IDataExportResponse } from 'models/http/data-export';
import { IGetHistoryResponse } from 'models/http/history';
import {
  AssignTrips,
  CancelTrips,
  ClearDriverAndVehicle,
  DeleteTrips,
  ExportTrips,
  IAnalysisResponse,
  IAssignConfig,
  IAssignLogsResponse,
  ICancelTripsResponse,
  ICreateTripResponse,
  IDataExportTripFilters,
  IDeleteTripsResponse,
  IGetQueueEnabledResponse,
  IGetSingleTripResponse,
  IGetTripsExportResponse,
  IGetTripsReportResponse,
  IGetTripsRequestData,
  IGetTripsResponse,
  IMapRoutesResponse,
  IMergeTripsResponse,
  IMoveReservationResponse,
  IRemoveReservationFromTripResponse,
  IReorderStopResponse,
  IRouteAssignFilters,
  IRouteAssignResponse,
  IRouteStatusResponse,
  ISwitchRouteResponse,
  ITripCreate,
  ITripCreateAsignOption,
  ITripCreateRequestFilters,
  ITripCreateRoutingOptions,
  ITripMoveReservationResponse,
  IUpdateStatusesResponse,
  IUpdateTripDriverResponse,
  IUpdateTripsStatusesRequest,
  IUpdateTripVehicleResponse,
  MergeTrips,
  SendTrips,
  TripReport,
  TripsJoinModes,
} from 'models/http/trip';
import { HttpTimeoutPriority } from 'utils/enums/http-timeout-priority';
import { prepareFiltersOfSelection, prepareQPFromFilters, serverAxios } from '../../http';
import { ModelsNames } from 'utils/enums/model-names';
import { ManualWaypoint, TripStopTypes } from 'pages/Trips/Trips.model';
import { IReleaseRoutingResponse, IReleaseRoutingReservationsRequest } from 'models/http/job';
import { TripStatus } from 'utils/enums/trip-statuses';
import { AddManualWaypointsResponse } from 'pages/Trips/TripsTripInfo/TripsTripInfoMap/EditOnMapUtils/EditOnMap.utils';

class TripHttp {
  public static DataExportRoute = `${ModelsNames.Trips}/data-export`;
  private static route = `/${ModelsNames.Trips}`;

  public async history(id: string, abortController?: AbortController): Promise<IGetHistoryResponse> {
    return serverAxios
      .get(`${TripHttp.route}/${id}/history`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Highest,
      })
      .then((response: AxiosResponse<IGetHistoryResponse>) => response.data);
  }

  public async get(filters: IGetTripsRequestData, abortController?: AbortController): Promise<IGetTripsResponse> {
    const query = prepareQPFromFilters(filters);

    return serverAxios
      .get(`${TripHttp.route}?${query.toString()}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetTripsResponse>) => {
        return response?.data;
      });
  }

  public async getById(id: number, abortController?: AbortController): Promise<IGetSingleTripResponse> {
    return serverAxios
      .get(`${TripHttp.route}/single/${id}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetSingleTripResponse>) => {
        return response.data;
      });
  }

  public async isQueueEnabled(abortController?: AbortController): Promise<IGetQueueEnabledResponse> {
    return serverAxios
      .get(`${TripHttp.route}/isQueueEnabled`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetQueueEnabledResponse>) => {
        return response.data;
      });
  }

  public async startTrip(id: number): Promise<void> {
    return serverAxios
      .put(`${TripHttp.route}/${id}/start`, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<void>) => {
        return response.data;
      });
  }

  public async endTrip(id: number): Promise<void> {
    return serverAxios
      .put(`${TripHttp.route}/${id}/end`, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<void>) => {
        return response.data;
      });
  }

  public async assignLog(id: number, abortController?: AbortController): Promise<IAssignLogsResponse['logs']> {
    return serverAxios
      .get(`${TripHttp.route}/single/${id}/assignLogs`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IAssignLogsResponse>) => {
        return response.data.logs;
      });
  }

  public async removeVehicle(id: number): Promise<void> {
    return serverAxios
      .delete(`${TripHttp.route}/${id}/vehicle`, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then(() => {
        return;
      });
  }

  public async updateVehicle(id: number, vehicleId: number): Promise<IUpdateTripVehicleResponse> {
    return serverAxios
      .put(
        `${TripHttp.route}/${id}/vehicle`,
        { vehicleId },
        {
          timeout: HttpTimeoutPriority.Medium,
        },
      )
      .then((response: AxiosResponse<IUpdateTripVehicleResponse>) => {
        return response.data;
      });
  }

  public async updateDriver(id: number, driverId: number): Promise<IUpdateTripDriverResponse> {
    return serverAxios
      .put(
        `${TripHttp.route}/${id}/driver`,
        { driverId },
        {
          timeout: HttpTimeoutPriority.Medium,
        },
      )
      .then((response: AxiosResponse<IUpdateTripDriverResponse>) => {
        return response.data;
      });
  }

  public async updateStatus(id: number, status: string): Promise<void> {
    return serverAxios
      .patch(
        `${TripHttp.route}/${id}/status`,
        { status },
        {
          timeout: HttpTimeoutPriority.Medium,
        },
      )
      .then(() => {
        return;
      });
  }

  public async updateStatuses(ids: number[], status: TripStatus): Promise<IUpdateStatusesResponse>;
  public async updateStatuses(filters: IUpdateTripsStatusesRequest, status: TripStatus): Promise<IUpdateStatusesResponse>;
  public async updateStatuses(filters: number[] | IUpdateTripsStatusesRequest, status: TripStatus): Promise<IUpdateStatusesResponse> {
    return serverAxios
      .patch(`${TripHttp.route}/bulk-status`, { ...prepareFiltersOfSelection(filters), status })
      .then((response: AxiosResponse<IUpdateStatusesResponse>) => {
        return response.data;
      });
  }

  public async reorderStops(id: number, newStopIndex: number, oldStopIndex: number): Promise<IReorderStopResponse> {
    return serverAxios
      .post(
        `${TripHttp.route}/${id}/reorderStops`,
        { newStopIndex, oldStopIndex },
        {
          timeout: HttpTimeoutPriority.Medium,
        },
      )
      .then((response: AxiosResponse<IReorderStopResponse>) => {
        return response.data;
      });
  }

  public async moveReservation(
    id: number,
    reservationId: number,
    stopIndex: number,
    dateType: TripStopTypes,
  ): Promise<IMoveReservationResponse> {
    return serverAxios
      .post(
        `${TripHttp.route}/${id}/moveReservation`,
        { reservationId, stopIndex, dateType },
        {
          timeout: HttpTimeoutPriority.Medium,
        },
      )
      .then((response: AxiosResponse<IMoveReservationResponse>) => {
        return response.data;
      });
  }

  public async routeStatus(prod: string, doLog: boolean): Promise<IRouteStatusResponse> {
    return serverAxios
      .post(
        `${TripHttp.route}/routestatus`,
        { prod, doLog },
        {
          timeout: HttpTimeoutPriority.Medium,
        },
      )
      .then((response: AxiosResponse<IRouteStatusResponse>) => {
        return response.data;
      });
  }

  public async analysis(id: number, abortController?: AbortController): Promise<IAnalysisResponse[]> {
    return serverAxios
      .get(`${TripHttp.route}/${id}/analysis`, {
        timeout: HttpTimeoutPriority.Medium,
        signal: abortController?.signal,
      })
      .then((response: AxiosResponse<IAnalysisResponse[]>) => {
        return response.data;
      });
  }

  public async favorite(id: number): Promise<void> {
    return serverAxios
      .put(`${TripHttp.route}/${id}/favorite`, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then(() => {
        return;
      });
  }

  public async unfavorite(id: number): Promise<void> {
    return serverAxios
      .delete(`${TripHttp.route}/${id}/favorite`, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then(() => {
        return;
      });
  }

  public async updateSupplier(id: number, supplier: number): Promise<void> {
    return serverAxios
      .patch(
        `${TripHttp.route}/${id}/supplier`,
        { supplierId: supplier },
        {
          timeout: HttpTimeoutPriority.Medium,
        },
      )
      .then(() => {
        return;
      });
  }

  // Export to csv
  public async export(ids: number[], abortController?: AbortController): Promise<IGetTripsExportResponse>;
  public async export(filters: ExportTrips, cabortController?: AbortController): Promise<IGetTripsExportResponse>;
  public async export(filters: number[] | ExportTrips, abortController?: AbortController): Promise<IGetTripsExportResponse> {
    return serverAxios
      .get(`${TripHttp.route}/export`, {
        data: prepareFiltersOfSelection(filters),
        timeout: HttpTimeoutPriority.Medium,
        signal: abortController?.signal,
      })
      .then((response: AxiosResponse<IGetTripsExportResponse>) => {
        return response.data;
      });
  }

  public async assignManual(ids: number[], driverId: number, vehicleId: number): Promise<{ assignedCount: number }>;
  public async assignManual(filters: AssignTrips, driverId: number, vehicleId: number): Promise<{ assignedCount: number }>;
  public async assignManual(filters: number[] | AssignTrips, driverId: number, vehicleId: number): Promise<{ assignedCount: number }> {
    return serverAxios
      .patch(`${TripHttp.route}/manualAssign`, {
        ...prepareFiltersOfSelection(filters),
        driverId,
        vehicleId,
      })
      .then((response: AxiosResponse<{ assignedCount: number }>) => {
        return response.data;
      });
  }

  public async assigenVehiclesDrivers(
    ids: number[],
    config: IAssignConfig,
    abortController?: AbortController,
  ): Promise<IRouteAssignResponse>;
  public async assigenVehiclesDrivers(
    filters: IRouteAssignFilters,
    config: IAssignConfig,
    abortController?: AbortController,
  ): Promise<IRouteAssignResponse>;
  public async assigenVehiclesDrivers(
    filters: number[] | IRouteAssignFilters,
    config: IAssignConfig,
    abortController?: AbortController,
  ): Promise<IRouteAssignResponse>;
  public async assigenVehiclesDrivers(
    filters: number[] | IRouteAssignFilters,
    config: IAssignConfig,
    abortController?: AbortController,
  ): Promise<IRouteAssignResponse> {
    return serverAxios
      .post(
        `${TripHttp.route}/assign`,
        {
          ...prepareFiltersOfSelection(filters),
          config,
        },
        {
          timeout: HttpTimeoutPriority.Medium,
          signal: abortController?.signal,
        },
      )
      .then((response: AxiosResponse<IRouteAssignResponse>) => {
        return response.data;
      });
  }

  public async mergeTrips(ids: number[]): Promise<IMergeTripsResponse>; //TODO change id to array of numbers
  public async mergeTrips(filters: MergeTrips): Promise<IMergeTripsResponse>;
  public async mergeTrips(filters: number[] | MergeTrips): Promise<IMergeTripsResponse> {
    return serverAxios
      .post(`${TripHttp.route}/merge`, prepareFiltersOfSelection(filters))
      .then((response: AxiosResponse<IMergeTripsResponse>) => {
        return response.data;
      });
  }

  public async deleteTrips(ids: number[]): Promise<IDeleteTripsResponse>;
  public async deleteTrips(filters: DeleteTrips): Promise<IDeleteTripsResponse>;
  public async deleteTrips(filters: number[] | DeleteTrips): Promise<IDeleteTripsResponse> {
    return serverAxios
      .delete(`${TripHttp.route}`, {
        data: prepareFiltersOfSelection(filters),
      })
      .then((response: AxiosResponse<IDeleteTripsResponse>) => {
        return response.data;
      });
  }

  public async releaseRouting(ids: number[]): Promise<IReleaseRoutingResponse>;
  public async releaseRouting(filters: IReleaseRoutingReservationsRequest): Promise<IReleaseRoutingResponse>;
  public async releaseRouting(filters: number[] | IReleaseRoutingReservationsRequest): Promise<IReleaseRoutingResponse> {
    return serverAxios
      .post(`${TripHttp.route}/release-routing`, prepareFiltersOfSelection(filters))
      .then((response: AxiosResponse<IReleaseRoutingResponse>) => {
        return response.data;
      });
  }

  public async getRoutes(ids: number[]): Promise<IMapRoutesResponse> {
    const queryParams = prepareQPFromFilters({ ids });

    return serverAxios.get(`${TripHttp.route}/routes?${queryParams.toString()}`).then((response: AxiosResponse<IMapRoutesResponse>) => {
      return response.data;
    });
  }

  public async clearDriverAndVehicles(ids: number[], options?: { skipManuallyAssigned?: boolean }): Promise<void>;
  public async clearDriverAndVehicles(filters: ClearDriverAndVehicle, options?: { skipManuallyAssigned?: boolean }): Promise<void>;
  public async clearDriverAndVehicles(
    filters: number[] | ClearDriverAndVehicle,
    options?: { skipManuallyAssigned?: boolean },
  ): Promise<void> {
    return serverAxios
      .delete(`${TripHttp.route}/assignments`, {
        data: { ...prepareFiltersOfSelection(filters), skipManuallyAssigned: options?.skipManuallyAssigned },
      })
      .then(() => {
        return;
      });
  }

  public async removeSupplier(id: number, addRejected: boolean = false): Promise<void> {
    return serverAxios
      .delete(`${TripHttp.route}/${id}/supplier`, {
        timeout: HttpTimeoutPriority.Medium,
        data: addRejected ? { addRejected: true } : undefined,
      })
      .then(() => {
        return;
      });
  }

  public async routeCancel(ids: number[]): Promise<ICancelTripsResponse>; //TODO change id to array of numbers
  public async routeCancel(filters: CancelTrips): Promise<ICancelTripsResponse>;
  public async routeCancel(filters: number[] | CancelTrips): Promise<ICancelTripsResponse> {
    return serverAxios
      .patch(`${TripHttp.route}/cancel`, {
        ...prepareFiltersOfSelection(filters),
      })
      .then((response: AxiosResponse<ICancelTripsResponse>) => {
        return response.data;
      });
  }

  public async routeSend(ids: number[]): Promise<void>; //TODO change id to array of numbers
  public async routeSend(filters: SendTrips): Promise<void>;
  public async routeSend(filters: number[] | SendTrips): Promise<void> {
    return serverAxios
      .patch(`${TripHttp.route}/routeSend`, {
        ...prepareFiltersOfSelection(filters),
      })
      .then(() => {
        return;
      });
  }

  public async tripReport(ids: number[], abortController?: AbortController): Promise<IGetTripsReportResponse>; //TODO change id to array of numbers
  public async tripReport(filters: TripReport, abortController?: AbortController): Promise<IGetTripsReportResponse>;
  public async tripReport(filters: number[] | TripReport, abortController?: AbortController): Promise<IGetTripsReportResponse> {
    return serverAxios
      .get(`${TripHttp.route}/tripReport`, {
        data: prepareFiltersOfSelection(filters),

        timeout: HttpTimeoutPriority.Medium,
        signal: abortController?.signal,
      })
      .then((response: AxiosResponse<IGetTripsReportResponse>) => {
        return response.data;
      });
  }

  public async moveReservations(oldTripId: number, newTripId: number, reservationIds: number[]): Promise<ITripMoveReservationResponse> {
    return serverAxios
      .post(
        `${TripHttp.route}/moveReservations`,
        {
          oldTripId,
          newTripId,
          reservationIds,
        },
        { timeout: HttpTimeoutPriority.Medium },
      )
      .then((response: AxiosResponse<ITripMoveReservationResponse>) => {
        return response.data;
      });
  }

  public async removeReservations(tripId: number, reservationIds: number[]): Promise<IRemoveReservationFromTripResponse> {
    return serverAxios
      .delete(`${TripHttp.route}/${tripId}/reservations`, {
        data: { reservationIds },
        timeout: HttpTimeoutPriority.Low,
      })
      .then((response: AxiosResponse<IRemoveReservationFromTripResponse>) => {
        return response.data;
      });
  }

  public async changeStopLocation(tripId: number, stopId: string, address: IAddress): Promise<void> {
    return serverAxios
      .put(`${TripHttp.route}/${tripId}/changeStopLocation`, { stopId, address }, { timeout: HttpTimeoutPriority.Medium })
      .then(() => {
        return;
      });
  }
  public async addManualWaypoints(id: number, waypoints: ManualWaypoint[], abortController?: AbortController): Promise<AddManualWaypointsResponse> {
    return serverAxios
      .patch(
        `${TripHttp.route}/${id}/addManualWaypoints`,
        { waypoints },
        { timeout: HttpTimeoutPriority.Highest, signal: abortController?.signal },
      )
      .then((response: AxiosResponse<AddManualWaypointsResponse>) => response.data);
  }

  public async manualRoute(reservationIds: number[]): Promise<ICreateTripResponse> {
    return serverAxios
      .post(
        `${TripHttp.route}/manuallyRouteTrip`,
        { reservationIds },
        {
          timeout: HttpTimeoutPriority.Highest,
        },
      )
      .then((response: AxiosResponse<ICreateTripResponse>) => {
        return response.data;
      });
  }

  public async createTrips(
    ids: number[],
    joinMode: TripsJoinModes,
    doDistribute?: boolean,
    options?: ITripCreateRoutingOptions,
    assignOptions?: ITripCreateAsignOption,
    routeTemplateId?: string | null,
    assignTemplateId?: string | null,
    abortController?: AbortController,
  ): Promise<ICreateTripResponse>;
  public async createTrips(
    filters: ITripCreateRequestFilters,
    joinMode: TripsJoinModes,
    doDistribute?: boolean,
    options?: ITripCreateRoutingOptions,
    assignOptions?: ITripCreateAsignOption,
    routeTemplateId?: string | null,
    assignTemplateId?: string | null,
    abortController?: AbortController,
  ): Promise<ICreateTripResponse>;
  public async createTrips(
    filters: number[] | ITripCreateRequestFilters,
    joinMode: TripsJoinModes,
    doDistribute?: boolean,
    options?: ITripCreateRoutingOptions,
    assignOptions?: ITripCreateAsignOption,
    routeTemplateId?: string | null,
    assignTemplateId?: string | null,
    abortController?: AbortController,
  ): Promise<ICreateTripResponse>;
  public async createTrips(
    filters: number[] | ITripCreateRequestFilters,
    joinMode: TripsJoinModes,
    doDistribute?: boolean,
    options?: ITripCreateRoutingOptions,
    assignOptions?: ITripCreateAsignOption,
    routeTemplateId?: string | null,
    assignTemplateId?: string | null,
    abortController?: AbortController,
  ): Promise<ICreateTripResponse> {
    const preparedData: ITripCreate = {
      ...(Array.isArray(filters) ? { reservationIds: filters, useFilters: false } : { reservationFilters: filters, useFilters: true }),
      ...options,
      joinMode,
    };

    if (assignOptions) {
      preparedData.assignConfig = assignOptions;
    }

    if (routeTemplateId) {
      preparedData.routeTemplateId = Number(routeTemplateId);
    }

    if (assignTemplateId) {
      preparedData.assignTemplateId = Number(assignTemplateId);
    }

    if (doDistribute) {
      preparedData.doDistribute = true;
    }

    return serverAxios
      .post(`${TripHttp.route}`, preparedData, {
        timeout: HttpTimeoutPriority.Highest,
        signal: abortController?.signal,
      })
      .then((response: AxiosResponse<ICreateTripResponse>) => {
        return response.data;
      });
  }

  public async switchRoute(id: number, routeIndex: number, abortController?: AbortController): Promise<ISwitchRouteResponse> {
    return serverAxios
      .put(
        `${TripHttp.route}/mapRoute/${id}`,
        { index: routeIndex },
        { timeout: HttpTimeoutPriority.Highest, signal: abortController?.signal },
      )
      .then((response: AxiosResponse<ISwitchRouteResponse>) => response.data);
  }

  public async updateOperators(id: number, operators: string[], abortController?: AbortController): Promise<void> {
    return serverAxios.patch(
      `${TripHttp.route}/operators/${id}`,
      { operators },
      { timeout: HttpTimeoutPriority.Highest, signal: abortController?.signal },
    );
  }

  // TODO: move to useExportToOptibus? see similar solution in useExportToS3
  // Note: this is here to specifically to support exporting trips to Optibus
  public async dataExport(ids: number[], args: ExportRequestArgs, abortController?: AbortController): Promise<IDataExportResponse>;
  public async dataExport(
    filters: IDataExportTripFilters,
    type: ExportRequestArgs,
    abortController?: AbortController,
  ): Promise<IDataExportResponse>;
  public async dataExport(
    filters: number[] | IDataExportTripFilters,
    type: ExportRequestArgs,
    abortController?: AbortController,
  ): Promise<IDataExportResponse>;
  public async dataExport(
    filters: number[] | IDataExportTripFilters,
    type: ExportRequestArgs,
    abortController?: AbortController,
  ): Promise<IDataExportResponse> {
    const options = { timeout: HttpTimeoutPriority.Medium, signal: abortController?.signal };

    return serverAxios
      .post(`${TripHttp.route}/data-export`, { ...prepareFiltersOfSelection(filters), ...type }, options)
      .then((response: AxiosResponse<IDataExportResponse>) => response.data);
  }
}

export default TripHttp;
